import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Search, Check, X, Percent, RefreshCw, Filter, Info } from 'lucide-react';
import { neonQuery } from '../services/api';
import { getCurrentUser, getAccessToken } from '../services/auth';

const BC_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:8888/.netlify/functions/bc-proxy'
  : '/.netlify/functions/bc-proxy';

const WOS_URL = process.env.NODE_ENV === 'development'
  ? 'http://localhost:8888/.netlify/functions/wos-proxy'
  : '/.netlify/functions/wos-proxy';

// --- What the website means by "Market Price" and "VIP Price" -----------------
//
// Matt Sr, 2026-09-08, asked for the two price columns here to be named the way
// the website names them. Checked against the live store before renaming, and
// the mapping is NOT the intuitive one:
//
//   BigCommerce `price`       -> the STRUCK-THROUGH "was" figure. It is only the
//                                market price on a product carrying no sale
//                                price (263 of 1,277 SKUs on 2026-09-08).
//   BigCommerce `sale_price`  -> the MARKET PRICE. The storefront card writes
//                                `data-market-price="{{price.with_tax.value}}"`,
//                                which is the sale price whenever one is set,
//                                and this page's own margin maths already treats
//                                it that way (`effectivePrice` below).
//   VIP price                 -> NOT STORED IN ANY FIELD. It is BigCommerce
//                                AUTOMATIC promotion #24 "Existing VIP 8% off"
//                                (customer group 1), applied at checkout on top
//                                of the market price. `exclude_items_on_sale` is
//                                false, so it rides on the sale price.
//
// THEREFORE THE VIP COLUMN IS DERIVED AND READ ONLY. Writing market x 0.92 into
// `sale_price` would hand the VIP price to every non-member AND stack promotion
// #24 on top of it, so a member would pay 15.36% off, not 8%.
//
// THE PERCENTAGE IS READ OFF PROMOTION #24, NEVER A HARDCODED 8. Same rule the
// storefront follows (`vipGlobalDiscount` from theme setting `vip_global_discount`);
// the PDP badge has drifted from the real checkout number before precisely
// because a second copy of the number lived somewhere else. The fallback below is
// only used when the promotion cannot be read, and the page says so when it is.
const VIP_PROMOTION_ID = 24;
const VIP_DISCOUNT_FALLBACK_PCT = 8;

// Promotion #24 excludes these two products, so they have no VIP price at all.
// A VIP figure on them would be a price nobody can pay.
//   153 = VIP Club Membership, 240 = Gift Card by Mail
const VIP_EXCLUDED_PRODUCT_IDS = new Set([153, 240]);

async function bcApi(method, path, body) {
  const token = getAccessToken();
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(BC_URL, {
    method: 'POST',
    headers,
    body: JSON.stringify({ method, path, body }),
  });
  const data = await res.json();
  if (!res.ok) {
    const detail = data?.errors ? JSON.stringify(data.errors) : data?.title || data?.error || 'BC API error';
    throw new Error(detail);
  }
  return data;
}

function wosGet(endpoint) {
  return fetch(WOS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ endpoint }),
  }).then(r => r.json());
}

// Extract WOS item code from BC SKU
// BC SKUs like "20ZMCB-1DZ-CP018" or "5T1BC-CWKC-HD-LI-CP068C" contain a "CPxxx" fragment
// WOS uses "CP001", "CP016", "CP068C", etc. as the item key
function extractWosKeys(bcSku) {
  if (!bcSku) return [];
  const match = bcSku.toUpperCase().match(/(?:^|[-])((CP\d+[A-Z]?))/);
  if (!match) return [];
  const raw = match[1];
  const keys = [raw];
  const stripped = raw.replace(/^(CP)0+(\d+[A-Z]?)$/, '$1$2');
  if (stripped !== raw) {
    keys.push(stripped);
    const oneZero = raw.replace(/^(CP)0{2,}(\d+[A-Z]?)$/, '$10$2');
    if (oneZero !== raw && oneZero !== stripped) keys.push(oneZero);
  }
  const noTrailLetter = raw.replace(/([A-Z])$/, '');
  if (noTrailLetter !== raw) keys.push(noTrailLetter);
  return keys;
}

function lookupWos(sku, map, overrides = {}) {
  // Check override first
  const override = overrides[sku.toUpperCase()];
  if (override && map[override] != null) return { cost: map[override], key: override };
  // Fall back to auto-detect from SKU
  const keys = extractWosKeys(sku);
  for (const k of keys) {
    if (map[k] != null) return { cost: map[k], key: k };
  }
  return { cost: null, key: keys[0] || null };
}

// Extract quantity multiplier from BC SKU
// Weight: 10LBS, 5LBS, 3LBS, 2LBS, 1LB
// Count: 2DZ (24), 1DZ/DZ (12), HD (6 = half dozen)
function getMultiplier(sku) {
  const s = sku.toUpperCase();
  const lbMatch = s.match(/(\d+)LBS?(?:-|$)/);
  if (lbMatch) return parseInt(lbMatch[1]);
  if (/-2DZ-|-2DZ$/.test(s)) return 24;
  if (/-1DZ-|-1DZ$/.test(s)) return 12;
  if (/-DZ-|-DZ$/.test(s)) return 12;
  if (/-HD-|-HD$/.test(s)) return 6;
  return 1;
}

// --- Pack size and crab grade -----------------------------------------------
// Matt Sr asked (2026-08-26) to sort this page by pack size first and to filter
// it down to one grade at a time. Both values are read from the BC variant
// OPTION LABEL first and from the SKU token second. That is not belt-and-braces
// for its own sake: six products (163-180, "Colossal Male Hard Crabs" etc.)
// carry the grade in the PRODUCT NAME and have no grade option at all, so the
// SKU is the only place it exists for them. The two readings were checked
// against every crab variant in the live catalog on 2026-08-26 - 684 of 684
// agreed on pack size, 684 of 684 agreed on grade, zero disagreements.

// Ascending by how many crabs are in the box. Matt listed 1/2 Dozen, Dozen,
// 1/2 Bushel, Bushel; 2 Dozen exists on product 112 and sits where its size puts it.
const PACK_ORDER = ['1/2 Dozen', 'Dozen', '2 Dozen', '1/2 Bushel', 'Bushel'];

// Exact label match, never a substring - "1/2 Bushel" contains "Bushel".
const PACK_BY_LABEL = {
  '1/2 dozen': '1/2 Dozen',
  'dozen': 'Dozen',
  '1 dozen': 'Dozen',
  '2 dozen': '2 Dozen',
  'two dozen': '2 Dozen',
  '1/2 bushel': '1/2 Bushel',
  'half bushel': '1/2 Bushel',
  'bushel': 'Bushel',
};
const PACK_BY_SKU = { HD: '1/2 Dozen', DZ: 'Dozen', '1DZ': 'Dozen', '2DZ': '2 Dozen', HB: '1/2 Bushel', BU: 'Bushel' };
const PACK_SKU_RE = /-(HD|2DZ|1DZ|DZ|HB|BU)(?=-|$)/;
// The options that carry a pack size on this catalog.
const PACK_OPTION_RE = /^(quantity|serving size|amount)$/i;

function parsePackSize(sku, optionValues) {
  for (const o of optionValues || []) {
    if (!PACK_OPTION_RE.test(o.option_display_name || '')) continue;
    const hit = PACK_BY_LABEL[(o.label || '').trim().toLowerCase()];
    if (hit) return hit;
    // A pack size we don't rank (lbs, packs, containers) - keep the label as-is
    // so those rows still group together instead of scattering.
    if (o.label) return o.label.trim();
  }
  const m = (sku || '').toUpperCase().match(PACK_SKU_RE);
  return m ? PACK_BY_SKU[m[1]] : null;
}

function packRank(pack) {
  if (!pack) return 99;                       // no pack size at all - sorts last
  const i = PACK_ORDER.indexOf(pack);
  return i === -1 ? 50 : i;                   // unranked packs sit after the crab ones
}

// Matt's order, smallest crab to largest, males and females interleaved:
// Standards, Regular, Selects, Large, Premiums, Colossals.
const CRAB_GRADE_ORDER = ['Standard Males', 'Standard Females', 'Regular Females', 'Select Males', 'Select Females', 'Large Females', 'Premium Males', 'Colossal Males'];
const GRADE_BY_SKU = { STM: 'Standard Males', STF: 'Standard Females', SLF: 'Select Females', RF: 'Regular Females', SLM: 'Select Males', LF: 'Large Females', PM: 'Premium Males', CM: 'Colossal Males' };
// The optional LV/ST prefix is Live/Steamed glued onto the grade (LVCMHC, STCMHC).
// STMHC still reads as Standard Male because "MHC" is not one of the grades.
const GRADE_SKU_RE = /(?:^|-)(?:LV|ST)?(CM|PM|SLM|STM|SLF|STF|LF|RF)HC(?:-|$)/;
// Grade-bearing options. "Type" is deliberately absent - on this catalog it means
// Fresh/Pasteurized and Original/All-Jumbo, which are not grades.
const GRADE_OPTION_RE = /^(crab size|crab type|soft crab size|scallop size|lobster tail size)$/i;

function parseGrade(sku, optionValues) {
  // SKU first for hard crabs: it is the only reading that distinguishes male from
  // female, which is how Matt names them ("Colossal Males", "Large Females").
  const m = (sku || '').toUpperCase().match(GRADE_SKU_RE);
  if (m) return GRADE_BY_SKU[m[1]];
  for (const o of optionValues || []) {
    if (GRADE_OPTION_RE.test(o.option_display_name || '') && o.label) return o.label.trim();
  }
  return null;
}

function gradeRank(grade) {
  if (!grade) return 99;
  const i = CRAB_GRADE_ORDER.indexOf(grade);
  return i === -1 ? 50 : i;
}

// Strip the parenthetical so variants of one product group under its base name.
const baseName = (name) => (name || '').replace(/\s*\(.*$/, '');

// 'pack'    = Matt's order: pack size, then grade, then product.
// 'product' = the original order: product, then variant type, then price.
function sortRows(rows, mode) {
  const out = rows.slice();
  if (mode === 'pack') {
    out.sort((a, b) => {
      if (a.packRank !== b.packRank) return a.packRank - b.packRank;
      if (a.packRank === 50) {                       // unranked packs: alpha by label
        const c = (a.packSize || '').localeCompare(b.packSize || '');
        if (c !== 0) return c;
      }
      if (a.gradeRank !== b.gradeRank) return a.gradeRank - b.gradeRank;
      if (a.gradeRank === 50) {
        const c = (a.grade || '').localeCompare(b.grade || '');
        if (c !== 0) return c;
      }
      const c1 = baseName(a.productName).localeCompare(baseName(b.productName));
      if (c1 !== 0) return c1;
      const c2 = a.productName.localeCompare(b.productName);
      if (c2 !== 0) return c2;
      return (a.price || 0) - (b.price || 0);
    });
    return out;
  }
  // Original: group by product, then variant type (Original before All-Jumbo), then price.
  const variantType = (name) => {
    const match = name.match(/\(([^,)]+)/);
    return match ? match[1].trim() : '';
  };
  out.sort((a, b) => {
    if (a.productId !== b.productId) {
      return baseName(a.productName).localeCompare(baseName(b.productName));
    }
    const typeA = variantType(a.productName);
    const typeB = variantType(b.productName);
    const origA = typeA.toLowerCase().includes('original') ? 0 : 1;
    const origB = typeB.toLowerCase().includes('original') ? 0 : 1;
    if (origA !== origB) return origA - origB;
    const typeCmp = typeA.localeCompare(typeB);
    if (typeCmp !== 0) return typeCmp;
    return (a.price || 0) - (b.price || 0);
  });
  return out;
}

async function fetchAllPages(basePath) {
  let page = 1;
  let all = [];
  while (true) {
    const sep = basePath.includes('?') ? '&' : '?';
    const res = await bcApi('GET', `${basePath}${sep}page=${page}&limit=250`);
    const items = res?.data || [];
    all = all.concat(items);
    if (!res?.meta?.pagination || page >= res.meta.pagination.total_pages) break;
    page++;
  }
  return all;
}

// Shows original price as strikethrough + delta when a price has been changed
function PriceCell({ value, original, onChange, onBlur, changed, borderColor = 'var(--orange)', bgColor = '#fff8f5' }) {
  const delta = changed ? (parseFloat(value) - original) : 0;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
      {changed && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 11, lineHeight: 1 }}>
          <span style={{ textDecoration: 'line-through', color: 'var(--gray-400)' }}>${original.toFixed(2)}</span>
          <span style={{ color: delta > 0 ? '#1a7a1a' : '#c53030', fontWeight: 600 }}>
            {delta > 0 ? '+' : ''}{delta.toFixed(2)}
          </span>
        </div>
      )}
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <span style={{ position: 'absolute', left: 6, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-400)', fontSize: 13, pointerEvents: 'none' }}>$</span>
        <input
          type="number"
          step="0.01"
          min="0"
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          style={{
            width: 100, textAlign: 'right', padding: '4px 8px 4px 20px',
            border: changed ? `2px solid ${borderColor}` : '1px solid var(--gray-200)',
            borderRadius: 4, fontSize: 13, background: changed ? bgColor : 'white',
          }}
        />
      </div>
    </div>
  );
}

// One grade, one click. Chips rather than a dropdown because the list is short and
// the point of the request was to jump straight to a grade.
function GradeChip({ label, count, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '4px 10px', borderRadius: 999, cursor: 'pointer', fontSize: 12,
        fontWeight: active ? 600 : 500,
        border: active ? '1px solid var(--orange)' : '1px solid var(--gray-200)',
        background: active ? 'var(--orange)' : 'white',
        color: active ? 'white' : 'var(--gray-600)',
      }}
    >
      {label}
      <span style={{ fontSize: 11, opacity: active ? 0.85 : 0.6 }}>{count}</span>
    </button>
  );
}

export default function Pricing() {
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [enabledOnly, setEnabledOnly] = useState(true); // enabled + purchasable
  const [searchTerm, setSearchTerm] = useState('');
  const [sortMode, setSortMode] = useState('pack'); // 'pack' = Matt's order, 'product' = the original
  const [gradeFilter, setGradeFilter] = useState('all');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMsg, setLoadingMsg] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [edits, setEdits] = useState({}); // key -> { price?, salePrice?, cpCost? }
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null);
  // Bulk controls — Sale Price markup
  const [bulkValue, setBulkValue] = useState('');
  const [bulkRoundTo99, setBulkRoundTo99] = useState(true);
  // Default Price relative to Sale Price
  const [defaultMarkup, setDefaultMarkup] = useState(''); // e.g. "20" means Default = Sale / (1 - 20/100), i.e. Sale is "20% off"
  const [defaultRoundTo99, setDefaultRoundTo99] = useState(true);
  // The live VIP discount, read off BigCommerce promotion #24. See the note at
  // the top of this file for why it is never hardcoded here.
  const [vipDiscountPct, setVipDiscountPct] = useState(VIP_DISCOUNT_FALLBACK_PCT);
  const [vipDiscountLive, setVipDiscountLive] = useState(false);

  const categoryMap = useRef({});
  const wosCostMap = useRef({});
  const wosOverrides = useRef({}); // bc_sku → forced wos_key
  const cpCostMap = useRef({}); // sku -> cost from Neon
  const [dataReady, setDataReady] = useState(false);

  // Load categories, WOS costs, and CP costs on mount
  useEffect(() => {
    let catsLoaded = false, wosLoaded = false, cpLoaded = false;
    const checkReady = () => { if (catsLoaded && wosLoaded && cpLoaded) setDataReady(true); };

    // Categories
    (async () => {
      try {
        const cats = await fetchAllPages('/v3/catalog/categories');
        const sorted = cats.sort((a, b) => a.name.localeCompare(b.name));
        const map = {};
        sorted.forEach(c => { map[c.id] = c.name; });
        categoryMap.current = map;
        setCategories(sorted);
      } catch (e) { console.error('Failed to load categories:', e); }
      catsLoaded = true; checkReady();
    })();

    // WOS costs
    (async () => {
      try {
        const reportRes = await wosGet('/api/InventoryApi/InventoryReport');
        const items = Array.isArray(reportRes) ? reportRes : reportRes?.inventories ?? [];
        const map = {};
        items.forEach(r => {
          const cost = r.inventoryCost ?? null;
          if (cost == null) return;
          const key = (r.item || r.itemNumber || '').toUpperCase();
          if (key) map[key] = cost;
        });
        wosCostMap.current = map;
        // Also load overrides
        try {
          const overrides = await neonQuery('wos_overrides_all');
          const oMap = {};
          (overrides || []).forEach(r => { oMap[r.bc_sku.toUpperCase()] = r.wos_key.toUpperCase(); });
          wosOverrides.current = oMap;
        } catch (e) { console.error('Failed to load WOS overrides:', e); }
      } catch (e) { console.error('Failed to load WOS costs:', e); }
      wosLoaded = true; checkReady();
    })();

    // CP costs from Neon
    (async () => {
      try {
        const rows = await neonQuery('cp_costs_all');
        const map = {};
        rows.forEach(r => { map[r.sku] = parseFloat(r.cost); });
        cpCostMap.current = map;
      } catch (e) { console.error('Failed to load CP costs:', e); }
      cpLoaded = true; checkReady();
    })();
  }, []);

  // Load products
  const loadProducts = useCallback(async () => {
    setLoading(true);
    setSelected(new Set());
    setEdits({});
    setSaveResult(null);
    setGradeFilter('all'); // the grades on offer change with the category
    try {
      let path = '/v3/catalog/products?include=variants&include_fields=name,price,sale_price,is_visible,categories,sku';
      if (selectedCategory !== 'all') {
        path += `&categories:in=${selectedCategory}`;
      }
      if (enabledOnly) {
        path += '&is_visible=true';
      }

      setLoadingMsg('Loading products...');
      const products = await fetchAllPages(path);

      const rowData = [];
      for (const p of products) {
        const catNames = (p.categories || []).map(id => categoryMap.current[id] || `#${id}`).join(', ');
        if (p.variants && p.variants.length > 0) {
          for (const v of p.variants) {
            const purchasable = !v.purchasing_disabled;
            if (enabledOnly && !purchasable) continue;
            const sku = v.sku || p.sku || '';
            const wos = lookupWos(sku, wosCostMap.current, wosOverrides.current);
            const mult = getMultiplier(sku);
            const packSize = parsePackSize(sku, v.option_values);
            const grade = parseGrade(sku, v.option_values);
            rowData.push({
              packSize, packRank: packRank(packSize), grade, gradeRank: gradeRank(grade),
              productId: p.id,
              variantId: v.id,
              sku,
              productName: p.name + (p.variants.length > 1 ? ` (${v.option_values?.map(o => o.label).join(', ') || 'Default'})` : ''),
              categoryNames: catNames,
              price: v.price ?? p.price ?? 0,
              salePrice: v.sale_price ?? p.sale_price ?? 0,
              wosCost: wos.cost,
              wosKey: wos.key,
              multiplier: mult,
              isBase: mult === 1,
              cpCost: cpCostMap.current[sku] ?? null,
              isVisible: p.is_visible,
              purchasable,
              originalPrice: v.price ?? p.price ?? 0,
              originalSalePrice: v.sale_price ?? p.sale_price ?? 0,
              originalCpCost: cpCostMap.current[sku] ?? null,
            });
          }
        } else {
          const sku = p.sku || '';
          const wos = lookupWos(sku, wosCostMap.current, wosOverrides.current);
          const mult = getMultiplier(sku);
          const packSize = parsePackSize(sku, null);
          const grade = parseGrade(sku, null);
          rowData.push({
            packSize, packRank: packRank(packSize), grade, gradeRank: gradeRank(grade),
            productId: p.id,
            variantId: null,
            sku,
            productName: p.name,
            categoryNames: catNames,
            price: p.price ?? 0,
            salePrice: p.sale_price ?? 0,
            wosCost: wos.cost,
            wosKey: wos.key,
            multiplier: mult,
            isBase: mult === 1,
            cpCost: cpCostMap.current[sku] ?? null,
            isVisible: p.is_visible,
            purchasable: true,
            originalPrice: p.price ?? 0,
            originalSalePrice: p.sale_price ?? 0,
            originalCpCost: cpCostMap.current[sku] ?? null,
          });
        }
      }

      // Sorting moved to sortRows() + a useMemo so switching the sort does not refetch BC.
      setRows(rowData);
      setLoadingMsg('');
    } catch (e) {
      console.error('Failed to load products:', e);
      setLoadingMsg('Error loading products: ' + e.message);
    }
    setLoading(false);
  }, [selectedCategory, enabledOnly]);

  useEffect(() => {
    if (categories.length > 0 && dataReady) loadProducts();
  }, [loadProducts, categories, dataReady]);

  const rowKey = (r) => `${r.productId}-${r.variantId || 0}`;

  const getDisplayPrice = (r) => {
    const e = edits[rowKey(r)];
    return e?.price !== undefined ? e.price : r.price;
  };

  const getDisplaySalePrice = (r) => {
    const e = edits[rowKey(r)];
    return e?.salePrice !== undefined ? e.salePrice : r.salePrice;
  };

  const getDisplayCpCost = (r) => {
    const e = edits[rowKey(r)];
    return e?.cpCost !== undefined ? e.cpCost : (r.cpCost ?? '');
  };

  // The Market Price the website advertises: the sale price when one is set,
  // otherwise the default. This is exactly what the storefront card renders into
  // `data-market-price`, so the two cannot disagree.
  const getMarketPrice = (r) => {
    const sale = parseFloat(getDisplaySalePrice(r));
    const dflt = parseFloat(getDisplayPrice(r));
    return sale > 0 ? sale : dflt;
  };

  // Derived, never stored. Rounding matches the storefront's exactly
  // (`Math.round(market * (1 - pct/100) * 100) / 100`) so the figure here is the
  // figure on the product page, to the penny.
  const getVipPrice = (r) => {
    if (VIP_EXCLUDED_PRODUCT_IDS.has(r.productId)) return null;
    const market = getMarketPrice(r);
    if (!(market > 0)) return null;
    return Math.round(market * (1 - vipDiscountPct / 100) * 100) / 100;
  };

  // Read the discount a member actually gets, rather than trusting a constant.
  // A failure leaves the fallback in place and the header says it is unverified,
  // because silently showing 8% when the promotion has moved is the one outcome
  // this whole column exists to prevent.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await bcApi('GET', '/v3/promotions/' + VIP_PROMOTION_ID);
        const pct = parseFloat(res?.data?.rules?.[0]?.action?.cart_items?.discount?.percentage_amount);
        const enabled = res?.data?.status === 'ENABLED';
        if (!cancelled && enabled && pct > 0 && pct < 100) {
          setVipDiscountPct(pct);
          setVipDiscountLive(true);
        }
      } catch {
        /* keep the fallback; the header flags it as unverified */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handlePriceEdit = (r, field, value) => {
    const key = rowKey(r);
    setEdits(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: value === '' ? '' : value },
    }));
  };

  const handlePriceBlur = (r, field) => {
    const key = rowKey(r);
    const edit = edits[key];
    if (!edit || edit[field] === undefined || edit[field] === '') return;
    const num = parseFloat(edit[field]);
    if (isNaN(num)) {
      setEdits(prev => {
        const next = { ...prev };
        if (next[key]) {
          delete next[key][field];
          if (Object.keys(next[key]).length === 0) delete next[key];
        }
        return next;
      });
    } else {
      setEdits(prev => ({
        ...prev,
        [key]: { ...prev[key], [field]: parseFloat(num.toFixed(2)) },
      }));
    }
  };

  const sortedRows = useMemo(() => sortRows(rows, sortMode), [rows, sortMode]);

  // Search first, so the grade chip counts describe what the search left behind.
  const searchedRows = useMemo(() => {
    if (!searchTerm) return sortedRows;
    const term = searchTerm.toLowerCase();
    return sortedRows.filter(r => r.sku.toLowerCase().includes(term) || r.productName.toLowerCase().includes(term));
  }, [sortedRows, searchTerm]);

  // The grades actually present, in the same order the rows sort in.
  const grades = useMemo(() => {
    const counts = new Map();
    for (const r of searchedRows) {
      if (!r.grade) continue;
      counts.set(r.grade, (counts.get(r.grade) || 0) + 1);
    }
    return [...counts.entries()]
      .map(([grade, count]) => ({ grade, count, rank: gradeRank(grade) }))
      .sort((a, b) => (a.rank !== b.rank ? a.rank - b.rank : a.grade.localeCompare(b.grade)));
  }, [searchedRows]);

  const filteredRows = useMemo(
    () => (gradeFilter === 'all' ? searchedRows : searchedRows.filter(r => r.grade === gradeFilter)),
    [searchedRows, gradeFilter]
  );

  // A grade that is no longer on offer (category changed, search narrowed) must not
  // leave the table silently empty with the chip still lit.
  useEffect(() => {
    if (gradeFilter !== 'all' && !grades.some(g => g.grade === gradeFilter)) setGradeFilter('all');
  }, [grades, gradeFilter]);

  // Selection
  const toggleSelect = (r) => {
    const key = rowKey(r);
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === filteredRows.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filteredRows.map(rowKey)));
    }
  };

  const hasChanges = Object.keys(edits).length > 0;
  const changedCount = Object.keys(edits).length;

  // Track the last applied bulk so we can show it and prevent stacking
  const [appliedBulk, setAppliedBulk] = useState(null); // { value, roundTo99 }
  const [appliedDefault, setAppliedDefault] = useState(null); // { discount, roundTo99 }

  // Apply Sale Price markup — always from ORIGINAL values
  const applyBulk = () => {
    if (!bulkValue || selected.size === 0) return;
    const val = parseFloat(bulkValue);
    if (isNaN(val)) return;

    const round99 = (v) => {
      if (v <= 0) return 0;
      if (bulkRoundTo99) return Math.floor(v) + 0.99;
      return parseFloat(v.toFixed(2));
    };

    setEdits(prev => {
      const next = { ...prev };
      for (const r of rows) {
        const key = rowKey(r);
        if (selected.has(key)) {
          if (!next[key]) next[key] = {};
          let newSale = r.originalSalePrice * (1 + val / 100);
          if (newSale < 0) newSale = 0;
          next[key].salePrice = round99(newSale);
        } else if (next[key]?.salePrice !== undefined) {
          // Clear sale price edits on deselected rows
          delete next[key].salePrice;
          if (Object.keys(next[key]).length === 0) delete next[key];
        }
      }
      return next;
    });

    setAppliedBulk({ value: val, roundTo99: bulkRoundTo99 });
  };

  // Set Default Price so that Sale Price appears as X% off
  // If discount is 20%, Default = Sale / (1 - 0.20) = Sale / 0.80
  const applyDefaultMarkup = () => {
    if (!defaultMarkup || selected.size === 0) return;
    const discount = parseFloat(defaultMarkup);
    if (isNaN(discount) || discount >= 100 || discount <= 0) return;

    const round99 = (v) => {
      if (v <= 0) return 0;
      if (defaultRoundTo99) return Math.floor(v) + 0.99;
      return parseFloat(v.toFixed(2));
    };

    setEdits(prev => {
      const next = { ...prev };
      for (const r of rows) {
        const key = rowKey(r);
        if (selected.has(key)) {
          if (!next[key]) next[key] = {};
          // Use the current (possibly edited) sale price
          const currentSale = next[key]?.salePrice !== undefined ? parseFloat(next[key].salePrice) : r.salePrice;
          if (currentSale <= 0) continue;
          let newDefault = currentSale / (1 - discount / 100);
          next[key].price = round99(newDefault);
        } else if (next[key]?.price !== undefined) {
          // Clear default price edits on deselected rows
          delete next[key].price;
          if (Object.keys(next[key]).length === 0) delete next[key];
        }
      }
      return next;
    });

    setAppliedDefault({ discount, roundTo99: defaultRoundTo99 });
  };

  // Save changes to BigCommerce + CP costs to Neon
  const saveChanges = async () => {
    setSaving(true);
    setSaveResult(null);
    try {
      // --- Save BC price changes ---
      const byProduct = {};
      for (const [key, edit] of Object.entries(edits)) {
        const row = rows.find(r => rowKey(r) === key);
        if (!row) continue;
        const priceChanged = edit.price !== undefined && parseFloat(edit.price) !== row.originalPrice;
        const saleChanged = edit.salePrice !== undefined && parseFloat(edit.salePrice) !== row.originalSalePrice;
        if (!priceChanged && !saleChanged) continue;

        if (!byProduct[row.productId]) byProduct[row.productId] = { variants: [], productEdit: null };

        if (row.variantId) {
          const variantUpdate = { id: row.variantId };
          if (priceChanged) variantUpdate.price = parseFloat(edit.price);
          if (saleChanged) variantUpdate.sale_price = parseFloat(edit.salePrice);
          byProduct[row.productId].variants.push(variantUpdate);
        } else {
          const prodUpdate = {};
          if (priceChanged) prodUpdate.price = parseFloat(edit.price);
          if (saleChanged) prodUpdate.sale_price = parseFloat(edit.salePrice);
          byProduct[row.productId].productEdit = prodUpdate;
        }
      }

      let bcUpdated = 0;
      let errors = [];

      // Write CPx price intents before pushing to BC — the webhook uses these
      // to attribute incoming price changes to the CPx user instead of "external".
      const intentItems = [];
      for (const pid of Object.keys(byProduct)) {
        const entry = byProduct[pid];
        const changedBy = getCurrentUser()?.staffName || 'CPx';
        if (entry.productEdit) {
          intentItems.push({
            product_id: parseInt(pid, 10),
            variant_id: null,
            sku: null,
            expected_price: entry.productEdit.price ?? null,
            expected_sale_price: entry.productEdit.sale_price ?? null,
            changed_by: changedBy,
          });
        }
        for (const v of entry.variants) {
          intentItems.push({
            product_id: parseInt(pid, 10),
            variant_id: v.id,
            sku: null,
            expected_price: v.price ?? null,
            expected_sale_price: v.sale_price ?? null,
            changed_by: changedBy,
          });
        }
      }
      if (intentItems.length > 0) {
        try { await neonQuery('price_intent_add', { items: intentItems }); }
        catch(e) { console.warn('price_intent_add failed:', e.message); }
      }

      for (const pid of Object.keys(byProduct)) {
        const entry = byProduct[pid];
        try {
          if (entry.productEdit) {
            await bcApi('PUT', `/v3/catalog/products/${pid}`, entry.productEdit);
            bcUpdated++;
          }
          for (const v of entry.variants) {
            await bcApi('PUT', `/v3/catalog/products/${pid}/variants/${v.id}`, v);
            bcUpdated++;
          }
        } catch (e) {
          errors.push(`Product ${pid}: ${e.message}`);
        }
      }

      // --- Save CP cost changes to Neon ---
      const cpCostItems = [];
      for (const [key, edit] of Object.entries(edits)) {
        if (edit.cpCost === undefined) continue;
        const row = rows.find(r => rowKey(r) === key);
        if (!row || !row.sku) continue;
        const newCost = parseFloat(edit.cpCost);
        if (isNaN(newCost)) continue;
        if (newCost === row.originalCpCost) continue;
        cpCostItems.push({ sku: row.sku, cost: newCost });
      }

      let cpUpdated = 0;
      if (cpCostItems.length > 0) {
        try {
          const result = await neonQuery('cp_costs_bulk_upsert', { items: cpCostItems });
          cpUpdated = result?.[0]?.updated || cpCostItems.length;
          // Update local cache
          cpCostItems.forEach(({ sku, cost }) => { cpCostMap.current[sku] = cost; });
        } catch (e) {
          errors.push(`CP Costs: ${e.message}`);
        }
      }

      const totalUpdated = bcUpdated + cpUpdated;
      if (errors.length > 0) {
        setSaveResult({ type: 'error', message: `${totalUpdated} updated, ${errors.length} failed: ${errors[0]}` });
      } else if (totalUpdated === 0) {
        setSaveResult({ type: 'info', message: 'No changes to save' });
      } else {
        const parts = [];
        if (bcUpdated > 0) parts.push(`${bcUpdated} price${bcUpdated !== 1 ? 's' : ''}`);
        if (cpUpdated > 0) parts.push(`${cpUpdated} CP cost${cpUpdated !== 1 ? 's' : ''}`);
        setSaveResult({ type: 'success', message: `${parts.join(' + ')} updated successfully` });
        setEdits({});
        setAppliedBulk(null);
        setAppliedDefault(null);
        loadProducts();
      }
    } catch (e) {
      setSaveResult({ type: 'error', message: e.message });
    }
    setSaving(false);
  };

  const discardChanges = () => {
    setEdits({});
    setSaveResult(null);
    setAppliedBulk(null);
    setAppliedDefault(null);
    setBulkValue('');
    setDefaultMarkup('');
  };

  const fmt = (v) => v != null ? `$${Number(v).toFixed(2)}` : '--';

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1600 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--navy)', margin: 0 }}>Pricing</h1>
          <p style={{ fontSize: 13, color: 'var(--gray-500)', margin: '4px 0 0' }}>
            Edit BigCommerce product pricing by category
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {hasChanges && (
            <>
              <span style={{ fontSize: 13, color: 'var(--orange)', fontWeight: 600 }}>
                {changedCount} unsaved change{changedCount !== 1 ? 's' : ''}
              </span>
              <button className="btn btn-ghost" onClick={discardChanges} style={{ fontSize: 13 }}>
                <X size={14} /> Discard
              </button>
              <button className="btn btn-primary" onClick={saveChanges} disabled={saving} style={{ fontSize: 13 }}>
                {saving ? <><RefreshCw size={14} className="spin" /> Saving...</> : <><Check size={14} /> Save / Push to BC</>}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Save result banner */}
      {saveResult && (
        <div style={{
          padding: '10px 16px', borderRadius: 8, marginBottom: 16, fontSize: 13,
          background: saveResult.type === 'success' ? '#e6f9e6' : saveResult.type === 'error' ? '#fde8e8' : '#fff3e0',
          color: saveResult.type === 'success' ? '#1a7a1a' : saveResult.type === 'error' ? '#c53030' : '#b45309',
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          {saveResult.type === 'success' ? <Check size={14} /> : <X size={14} />}
          {saveResult.message}
          <button onClick={() => setSaveResult(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <X size={12} />
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ padding: '12px 16px', display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Filter size={14} color="var(--gray-500)" />
            <select
              className="form-select"
              value={selectedCategory}
              onChange={e => setSelectedCategory(e.target.value)}
              style={{ minWidth: 220, fontSize: 13 }}
            >
              <option value="all">All Categories</option>
              {categories.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 13, color: 'var(--gray-500)' }}>Sort</span>
            <select
              className="form-select"
              value={sortMode}
              onChange={e => setSortMode(e.target.value)}
              style={{ minWidth: 210, fontSize: 13 }}
            >
              <option value="pack">Pack size, then grade</option>
              <option value="product">Product name</option>
            </select>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={enabledOnly}
              onChange={e => setEnabledOnly(e.target.checked)}
              style={{ accentColor: 'var(--orange)' }}
            />
            Enabled & Purchasable only
          </label>

          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-400)' }} />
              <input
                className="form-input"
                placeholder="Search by SKU or product name..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={{ paddingLeft: 30, fontSize: 13 }}
              />
            </div>
          </div>

          <button className="btn btn-outline" onClick={loadProducts} disabled={loading} style={{ fontSize: 13 }}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {/* Grade filter - jump straight to one grade instead of scrolling the whole list */}
        {grades.length > 1 && (
          <div style={{
            padding: '10px 16px', borderTop: '1px solid var(--gray-100)',
            display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap',
          }}>
            <span style={{ fontSize: 12, color: 'var(--gray-500)', marginRight: 2 }}>Grade</span>
            <GradeChip label="All" count={searchedRows.length} active={gradeFilter === 'all'} onClick={() => setGradeFilter('all')} />
            {grades.map(g => (
              <GradeChip
                key={g.grade}
                label={g.grade}
                count={g.count}
                active={gradeFilter === g.grade}
                onClick={() => setGradeFilter(gradeFilter === g.grade ? 'all' : g.grade)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Bulk Price Controls */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-body" style={{ padding: '12px 16px' }}>
          {/* How the two editable columns map onto what the website shows. Named
              here because the mapping is not the intuitive one and repricing off
              the wrong column is a real money mistake. */}
          <div style={{
            display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 12,
            padding: '8px 10px', borderLeft: '3px solid var(--orange)',
            background: '#fff8f4', fontSize: 12, color: 'var(--gray-600)', lineHeight: 1.5,
          }}>
            <Info size={14} color="var(--orange)" style={{ flexShrink: 0, marginTop: 2 }} aria-hidden="true" />
            <span>
              <strong style={{ color: 'var(--navy)' }}>Market Price</strong> is what the website advertises
              as Today's Market and what a non-member pays. <strong style={{ color: 'var(--navy)' }}>Was</strong> is
              the struck-through figure beside it, not a price anyone pays.{' '}
              <strong style={{ color: 'var(--navy)' }}>VIP Price</strong> is worked out, not typed: members get
              {' '}{vipDiscountPct}% off the Market Price from BigCommerce automatic promotion #24 at checkout.
              {vipDiscountLive
                ? ' Read live from that promotion just now.'
                : ' BigCommerce could not be reached, so this is showing the last known ' + VIP_DISCOUNT_FALLBACK_PCT + '%. Check the promotion before repricing.'}
            </span>
          </div>

          {/* Row 1: Market Price markup */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <Percent size={14} color="var(--navy)" />
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)', whiteSpace: 'nowrap' }}>Market Price Markup</span>

            <div style={{ position: 'relative', width: 100 }}>
              <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-400)', fontSize: 13 }}>%</span>
              <input
                className="form-input"
                type="number"
                step="any"
                value={bulkValue}
                onChange={e => setBulkValue(e.target.value)}
                placeholder="e.g. 10"
                style={{ paddingLeft: 24, fontSize: 13, width: '100%' }}
              />
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={bulkRoundTo99} onChange={e => setBulkRoundTo99(e.target.checked)} style={{ accentColor: 'var(--orange)' }} />
              Round to .99
            </label>

            <button
              className="btn btn-primary"
              onClick={applyBulk}
              disabled={!bulkValue || selected.size === 0 || (appliedBulk && appliedBulk.value === parseFloat(bulkValue) && appliedBulk.roundTo99 === bulkRoundTo99)}
              style={{
                fontSize: 13, marginLeft: 'auto',
                ...( (!bulkValue || selected.size === 0 || (appliedBulk && appliedBulk.value === parseFloat(bulkValue) && appliedBulk.roundTo99 === bulkRoundTo99))
                  ? { opacity: 0.4, cursor: 'not-allowed' } : {}),
              }}
            >
              Apply to {selected.size} SKU{selected.size !== 1 ? 's' : ''}
            </button>
          </div>
          {appliedBulk && (
            <div style={{ fontSize: 12, color: 'var(--navy)', marginTop: 4, fontWeight: 600 }}>
              Market Price marked {appliedBulk.value > 0 ? 'up' : 'down'} {Math.abs(appliedBulk.value)}%{appliedBulk.roundTo99 ? ' (rounded to .99)' : ''} — change % to recalculate from originals
            </div>
          )}

          {/* Divider */}
          <div style={{ borderTop: '1px solid var(--gray-200)', margin: '10px 0' }} />

          {/* Row 2: Default Price relative to Sale Price */}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <Percent size={14} color="var(--orange)" />
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)', whiteSpace: 'nowrap' }}>Set Was price</span>
            <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>so Market Price shows as</span>

            <div style={{ position: 'relative', width: 100 }}>
              <span style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-400)', fontSize: 13 }}>%</span>
              <input
                className="form-input"
                type="number"
                step="any"
                min="1"
                max="99"
                value={defaultMarkup}
                onChange={e => setDefaultMarkup(e.target.value)}
                placeholder="e.g. 20"
                style={{ paddingLeft: 24, fontSize: 13, width: '100%' }}
              />
            </div>

            <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>off</span>

            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, cursor: 'pointer' }}>
              <input type="checkbox" checked={defaultRoundTo99} onChange={e => setDefaultRoundTo99(e.target.checked)} style={{ accentColor: 'var(--orange)' }} />
              Round to .99
            </label>

            <button
              className="btn btn-primary"
              onClick={applyDefaultMarkup}
              disabled={!defaultMarkup || selected.size === 0 || (appliedDefault && appliedDefault.discount === parseFloat(defaultMarkup) && appliedDefault.roundTo99 === defaultRoundTo99)}
              style={{
                fontSize: 13, marginLeft: 'auto',
                ...( (!defaultMarkup || selected.size === 0 || (appliedDefault && appliedDefault.discount === parseFloat(defaultMarkup) && appliedDefault.roundTo99 === defaultRoundTo99))
                  ? { opacity: 0.4, cursor: 'not-allowed' } : {}),
              }}
            >
              Apply to {selected.size} SKU{selected.size !== 1 ? 's' : ''}
            </button>
          </div>
          {appliedDefault && (
            <div style={{ fontSize: 12, color: 'var(--navy)', marginTop: 4, fontWeight: 600 }}>
              Was price set so Market Price appears {appliedDefault.discount}% off{appliedDefault.roundTo99 ? ' (rounded to .99)' : ''}
            </div>
          )}

          {/* Help text */}
          {selected.size === 0 && !appliedBulk && !appliedDefault && (
            <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 6 }}>
              Check the boxes below to select SKUs, then use the controls above. Use negative % on Market Price to decrease.
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="card">
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--gray-500)' }}>
            <RefreshCw size={20} className="spin" style={{ marginBottom: 8 }} />
            <div style={{ fontSize: 14 }}>{loadingMsg || 'Loading...'}</div>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table className="product-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ width: 36, textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={filteredRows.length > 0 && selected.size === filteredRows.length}
                        ref={el => { if (el) el.indeterminate = selected.size > 0 && selected.size < filteredRows.length; }}
                        onChange={selectAll}
                        style={{ accentColor: 'var(--orange)' }}
                      />
                    </th>
                    <th style={{ minWidth: 110 }}>SKU</th>
                    <th style={{ minWidth: 200 }}>Product Name</th>
                    <th style={{ minWidth: 100 }}>Category</th>
                    <th style={{ width: 70, textAlign: 'center' }}>
                      <span className="pricing-tooltip-wrap">
                        Base SKU
                        <span className="pricing-tooltip">Base SKU = 1:1 match to WOS item (1x unit). Non-base SKUs are multi-packs (HD, DZ, LBS).</span>
                      </span>
                    </th>
                    <th style={{ width: 80, textAlign: 'right' }}>WOS Cost</th>
                    <th style={{ width: 110, textAlign: 'right' }}>CP Cost</th>
                    <th style={{ width: 120, textAlign: 'right' }}>
                      <span className="pricing-tooltip-wrap">
                        Was
                        <span className="pricing-tooltip">BigCommerce "Default Price". This is the struck-through figure on the website, shown only when a lower Market Price is set. It is not what anyone pays.</span>
                      </span>
                    </th>
                    <th style={{ width: 120, textAlign: 'right' }}>
                      <span className="pricing-tooltip-wrap">
                        Market Price
                        <span className="pricing-tooltip">BigCommerce "Sale Price". This is what the website shows as Today's Market and what a non-member pays. Falls back to Was when it is blank.</span>
                      </span>
                    </th>
                    <th style={{ width: 120, textAlign: 'right' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                        VIP Price
                        <span className="pricing-tooltip-wrap">
                          <Info size={13} color="var(--gray-400)" aria-hidden="true" />
                          <span className="pricing-tooltip">Market Price less {vipDiscountPct}%. Worked out here, not stored: members get it from BigCommerce automatic promotion #24 at checkout, so it cannot be typed in and cannot drift.</span>
                        </span>
                      </span>
                    </th>
                    <th style={{ width: 70, textAlign: 'right' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                        Markup
                        <span className="pricing-tooltip-wrap">
                          <Info size={13} color="var(--gray-400)" aria-hidden="true" />
                          <span className="pricing-tooltip">Markup = (Market Price - CP Cost) / CP Cost x 100%</span>
                        </span>
                      </span>
                    </th>
                    <th style={{ width: 50, textAlign: 'center' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.length === 0 ? (
                    <tr>
                      <td colSpan={12} style={{ textAlign: 'center', padding: 32, color: 'var(--gray-400)' }}>
                        {rows.length === 0 ? 'No products found' : 'No products match your search'}
                      </td>
                    </tr>
                  ) : (
                    filteredRows.map((r, idx) => {
                      const key = rowKey(r);
                      // When the rows are grouped by pack size, say so in the table.
                      const groupHeader = sortMode === 'pack' && (idx === 0 || filteredRows[idx - 1].packSize !== r.packSize)
                        ? (r.packSize || 'No pack size')
                        : null;
                      const isSelected = selected.has(key);
                      const edit = edits[key];
                      const priceChanged = edit?.price !== undefined && parseFloat(edit.price) !== r.originalPrice;
                      const saleChanged = edit?.salePrice !== undefined && parseFloat(edit.salePrice) !== r.originalSalePrice;
                      const cpCostChanged = edit?.cpCost !== undefined && parseFloat(edit.cpCost) !== r.originalCpCost;

                      // Markup based on CP Cost: (price - cost) / cost
                      const displaySale = parseFloat(getDisplaySalePrice(r));
                      const displayPrice = parseFloat(getDisplayPrice(r));
                      const displayCpCost = parseFloat(getDisplayCpCost(r));
                      const effectivePrice = displaySale > 0 ? displaySale : displayPrice;
                      const vipPrice = getVipPrice(r);
                      const markup = !isNaN(displayCpCost) && displayCpCost > 0 && effectivePrice > 0
                        ? ((effectivePrice - displayCpCost) / displayCpCost * 100).toFixed(0)
                        : null;

                      return (
                        <React.Fragment key={key}>
                        {groupHeader && (
                          <tr>
                            <td colSpan={12} style={{
                              background: 'var(--gray-50, #f7f7f8)', borderTop: '1px solid var(--gray-200)',
                              padding: '6px 12px', fontSize: 12, fontWeight: 700, color: 'var(--navy)',
                              letterSpacing: 0.3, textTransform: 'uppercase',
                            }}>
                              {groupHeader}
                              <span style={{ fontWeight: 500, textTransform: 'none', color: 'var(--gray-500)', marginLeft: 8 }}>
                                {filteredRows.filter(x => x.packSize === r.packSize).length} SKUs
                              </span>
                            </td>
                          </tr>
                        )}
                        <tr style={{ background: isSelected ? 'rgba(220, 88, 42, 0.04)' : undefined }}>
                          <td style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSelect(r)}
                              style={{ accentColor: 'var(--orange)' }}
                            />
                          </td>
                          <td style={{ fontSize: 13, fontFamily: 'monospace', color: 'var(--gray-600)' }}>
                            {r.sku || <span style={{ color: 'var(--gray-300)' }}>--</span>}
                          </td>
                          <td style={{ fontSize: 13 }}>{r.productName}</td>
                          <td style={{ fontSize: 12, color: 'var(--gray-500)' }}>{r.categoryNames}</td>
                          <td style={{ textAlign: 'center', fontSize: 11 }}>
                            {r.isBase ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                <Check size={12} color="#22c55e" />
                                <span style={{ color: 'var(--gray-500)', fontFamily: 'monospace' }}>{r.wosKey}</span>
                              </span>
                            ) : (
                              <span style={{ color: 'var(--gray-400)' }}>{r.multiplier}x</span>
                            )}
                          </td>
                          <td style={{ textAlign: 'right', fontSize: 13, color: r.wosCost != null ? 'var(--gray-500)' : 'var(--gray-300)' }}>
                            {fmt(r.wosCost)}
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <PriceCell
                              value={getDisplayCpCost(r)}
                              original={r.originalCpCost ?? 0}
                              changed={cpCostChanged}
                              onChange={e => handlePriceEdit(r, 'cpCost', e.target.value)}
                              onBlur={() => handlePriceBlur(r, 'cpCost')}
                              borderColor="var(--navy)"
                              bgColor="#f0f4ff"
                            />
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <PriceCell
                              value={getDisplayPrice(r)}
                              original={r.originalPrice}
                              changed={priceChanged}
                              onChange={e => handlePriceEdit(r, 'price', e.target.value)}
                              onBlur={() => handlePriceBlur(r, 'price')}
                            />
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <PriceCell
                              value={getDisplaySalePrice(r)}
                              original={r.originalSalePrice}
                              changed={saleChanged}
                              onChange={e => handlePriceEdit(r, 'salePrice', e.target.value)}
                              onBlur={() => handlePriceBlur(r, 'salePrice')}
                            />
                          </td>
                          <td
                            style={{ textAlign: 'right', fontSize: 13, fontWeight: 600, color: vipPrice == null ? 'var(--gray-300)' : 'var(--navy)' }}
                            title={vipPrice == null
                              ? 'Promotion #24 does not discount this product, so there is no VIP price'
                              : 'Market ' + fmt(getMarketPrice(r)) + ' less ' + vipDiscountPct + '%'}
                          >
                            {vipPrice == null ? '--' : fmt(vipPrice)}
                          </td>
                          <td style={{
                            textAlign: 'right', fontSize: 13,
                            color: markup == null ? 'var(--gray-300)' : parseFloat(markup) < 50 ? '#c53030' : parseFloat(markup) < 100 ? '#b45309' : '#1a7a1a',
                            fontWeight: markup != null ? 600 : 400,
                          }}>
                            {markup != null ? `${markup}%` : '--'}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <span style={{
                              display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
                              background: r.isVisible && r.purchasable ? '#22c55e' : r.isVisible ? '#f59e0b' : '#d1d5db',
                            }} title={r.isVisible && r.purchasable ? 'Enabled & Purchasable' : r.isVisible ? 'Enabled, not purchasable' : 'Disabled'} />
                          </td>
                        </tr>
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
            {filteredRows.length > 0 && (
              <div style={{ padding: '10px 16px', fontSize: 12, color: 'var(--gray-500)', borderTop: '1px solid var(--gray-100)' }}>
                Showing {filteredRows.length} of {rows.length} SKU{rows.length !== 1 ? 's' : ''}
                {gradeFilter !== 'all' && <> &middot; filtered to <strong>{gradeFilter}</strong></>}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
